// src/components/files/FileItem.jsx
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getFileIconInfo, formatFileSize } from '../../services/file.service';
import { formatItemDate } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';
import InheritanceDropdownItem from '../common/InheritanceDropdownItem';
import PropertiesModal from '../common/PropertiesModal';

const FileItem = ({
  item,
  itemIndex,
  isSelected,
  onItemClick,
  onCheckboxChange,
  onDeleteClick,
  onDownloadClick,
  onAddToInheritanceClick,
  onRenameClick,
  viewMode = 'list',
  isAddingToInheritance = false,
  inheritanceStatus = {}
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const itemRef = useRef(null);
  const [localChecked, setLocalChecked] = useState(isSelected);
  
  // Add state for click handling
  const [clickTimeout, setClickTimeout] = useState(null);

  // Add useAuth hook
  const { user } = useAuth();
  const isMobile = window.innerWidth <= 768;
  const { iconClass, iconColor } = getFileIconInfo(item);
  const isFolder = item.type === 'folder';
  const uniqueId = `item-${item.id}-${item.name.replace(/[^a-zA-Z0-9]/g, '-')}`;

  useEffect(() => {
    setLocalChecked(isSelected);
  }, [isSelected]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        itemRef.current &&
        !itemRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fileId = `${item.name}-${item.path || ''}`;
    
    // Close dropdown when inheritance operation completes for this file
    if (inheritanceStatus.fileId === fileId && 
        !inheritanceStatus.loading && 
        (inheritanceStatus.success || inheritanceStatus.error)) {
      
      // Small delay to allow user to see the final state
      setTimeout(() => {
        setDropdownOpen(false);
      }, 1500);
    }
  }, [inheritanceStatus, item.name, item.path]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  const handleItemClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Set a timeout for single click action
    const timeout = setTimeout(() => {
      if (isFolder) {
        onItemClick(item, e);
      } else {
        setDropdownOpen(!dropdownOpen);
      }
      setClickTimeout(null);
    }, 250); // 250ms delay to detect double-click

    setClickTimeout(timeout);
  };

  const handleItemDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear the single-click timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Close dropdown if it's open
    setDropdownOpen(false);

    // Execute the view action (same as clicking "View" in dropdown)
    onItemClick(item, e);
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    const newChecked = e.target.checked;
    
    setLocalChecked(newChecked);
    
    onCheckboxChange(item, newChecked);
    
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  const handleActionClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    if (action !== 'addToInheritance') {
      setDropdownOpen(false);
    }
    switch (action) {
      case 'view':
        onItemClick(item, e);
        break;
      case 'rename':
        onRenameClick();
        break;
      case 'delete':
        onDeleteClick();
        break;
      case 'download':
        onDownloadClick();
        break;
      case 'addToInheritance':
        onAddToInheritanceClick();
        break;
      case 'properties':
        setPropertiesModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Check if user should see inheritance option
  const shouldShowInheritance = () => {
    // Try different approaches based on how subscription type is stored
    const subType = user?.SubscriptionType;
    
    // Check as numbers
    if (subType === 1 || subType === 3) return false;
    
    // Check as strings
    if (subType === '1' || subType === '3') return false;
    
    // Default to true if user is not loaded yet or subscription type is not restricted
    return true;
  };

  const renderDropdown = () => (
    <div
      ref={dropdownRef}
      className={`dropdown-menu dropdown-menu-right ${dropdownOpen ? 'show' : ''}`}
      style={{
        position: 'absolute',
        top: '25px',
        right: '0',
        zIndex: 1000,
        display: dropdownOpen ? 'block' : 'none'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {!isFolder && (
        <button className="dropdown-item" onClick={(e) => handleActionClick(e, 'view')}>
          <i className="fas fa-eye fa-sm fa-fw mr-2 text-black-400"></i>View
        </button>
      )}
      <button className="dropdown-item" onClick={(e) => handleActionClick(e, 'download')}>
        <i className="fas fa-download fa-sm fa-fw mr-2 text-black-400"></i>Download
      </button>
      
      {/* Conditionally render Add to Endowment based on subscription type */}
      {shouldShowInheritance() && (
        <InheritanceDropdownItem
          onClick={(e) => handleActionClick(e, 'addToInheritance')}
          isLoading={inheritanceStatus.loading && inheritanceStatus.fileId === `${item.name}-${item.path || ''}`}        />
      )}
      
      <div className="dropdown-divider"></div>
      
      {/* Properties button */}
      <button className="dropdown-item" onClick={(e) => handleActionClick(e, 'properties')}>
        <i className="fas fa-info-circle fa-sm fa-fw mr-2 text-black-400"></i>Properties
      </button>
      
      <button className="dropdown-item" onClick={(e) => handleActionClick(e, 'delete')}>
        <i className="fas fa-trash-alt fa-sm fa-fw mr-2 text-black-400"></i>Delete
      </button>
    </div>
  );

  // Grid view
  if (viewMode === 'grid') {
    return (
      <>
        <div
          className={`file-item-grid ${isSelected ? 'selected' : ''}`}
          onClick={handleItemClick}
          onDoubleClick={handleItemDoubleClick}
          ref={itemRef}
          data-type={item.type}
          data-name={item.name}
          data-path={item.path || item.name}
          style={{
            position: 'relative',
            display: 'inline-block',
            width: isMobile ? 'calc(33.33% - 16px)' : '120px',
            margin: '8px',
            textAlign: 'center',
            padding: '15px 8px',
            borderRadius: '5px',
            border: '1px solid #eee',
            backgroundColor: isSelected ? '#e8f5ff' : 'white',
            cursor: 'pointer',
            verticalAlign: 'top',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ position: 'absolute', top: '5px', left: '5px', zIndex: 2 }}>
            <input
              type="checkbox"
              id={`checkbox-${uniqueId}`}
              className="form-check-input item-checkbox"
              checked={localChecked} 
              onChange={(e) => {
                e.stopPropagation();
                onCheckboxChange(item, e.target.checked);
              }}
              style={{ width: '16px', height: '16px' }}
            />
          </div>
          <div className="item-actions" style={{ position: 'absolute', top: '5px', right: '5px', zIndex: 2 }}>
            <button
              type="button"
              className="btn btn-link p-0"
              style={{ color: '#00CC5C', fontSize: '14px', opacity: 1, visibility: 'visible' }}
              onClick={toggleDropdown}
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>
          </div>
          <div style={{ margin: '5px 0 10px 0' }}>
            {item.thumbnail ? (
              <img src={item.thumbnail} alt={item.name} style={{ width: '64px', height: '64px', objectFit: 'cover' }} />
            ) : (
              <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '36px' }}></i>
            )}
          </div>
          <div
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '12px',
              padding: '0 2px'
            }}
            title={item.name}
          >
            {item.name}
          </div>
          {!isFolder && !isMobile && (
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              {formatFileSize(item.size || 0)}
            </div>
          )}
          {renderDropdown()}
        </div>
        
        {/* Properties Modal */}
        <PropertiesModal
          isOpen={propertiesModalOpen}
          onClose={() => setPropertiesModalOpen(false)}
          itemData={item}
          itemType="file"
        />
      </>
    );
  }

  // List view
  return (
    <>
      <div
        className={`item d-flex align-items-center card-item-container ${isSelected ? 'selected' : ''}`}
        onClick={handleItemClick}
        onDoubleClick={handleItemDoubleClick}
        data-type={item.type}
        data-name={item.name}
        data-path={item.path || item.name}
        ref={itemRef}
        style={{
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: isSelected ? '#e8f5ff' : 'white',
          borderBottom: '1px solid #eee',
          padding: '8px 0'
        }}
      >
        <div className="item-content d-flex align-items-center flex-grow-1">
          <div className="item-check" style={{ width: isMobile ? '30px' : '40px', display: 'flex', justifyContent: 'center' }}>
            <input
              type="checkbox"
              id={`checkbox-${uniqueId}`}
              className="form-check-input item-checkbox"
              checked={localChecked} 
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                onCheckboxChange(item, e.target.checked);
              }}
              style={{ width: '18px', height: '18px', margin: '0' }}
            />
          </div>
          <div className="item-icon" style={{ width: isMobile ? '30px' : '40px', display: 'flex', justifyContent: 'center' }}>
            {item.thumbnail ? (
              <img src={item.thumbnail} alt={item.name} className="item-thumbnail" />
            ) : (
              <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '24px' }}></i>
            )}
          </div>
          <div className="item-name text-truncate" style={{ width: isMobile ? '45%' : '50%', paddingRight: '10px' }} title={item.name}>
            {item.name}
          </div>
          <div className="item-size text-right" style={{ width: isMobile ? '20%' : '15%' }}>
            {!isFolder && item.size !== undefined && formatFileSize(item.size)}
          </div>
          {!isFolder && !isMobile && (
            <div className="item-date text-right" style={{ width: '20%' }}>
              {item.lastEdited || item.lastModified ? (
                formatItemDate(item.lastEdited || item.lastModified)
              ) : (
                <span className="text-black">No date</span>
              )}
            </div>
          )}
        </div>
        <div className="item-actions" style={{ display: 'flex', alignItems: 'center', width: '40px' }}>
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ color: '#00CC5C', opacity: 1, visibility: 'visible' }}
            onClick={toggleDropdown}
          >
            <i className="fas fa-ellipsis-v"></i>
          </button>
        </div>
        {renderDropdown()}
      </div>
      
      {/* Properties Modal */}
      <PropertiesModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        itemData={item}
        itemType="file"
      />
    </>
  );
};

FileItem.propTypes = {
  item: PropTypes.object.isRequired,
  itemIndex: PropTypes.number,
  isSelected: PropTypes.bool,
  onItemClick: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired,
  onAddToInheritanceClick: PropTypes.func.isRequired,
  onRenameClick: PropTypes.func.isRequired,
  viewMode: PropTypes.string,
  isAddingToInheritance: PropTypes.bool,
  inheritanceStatus: PropTypes.object
};

export default FileItem;